// ---------------------------------------------------------------------------
// The lobby's setting rows.
//
// Split out of `LobbyScreen.tsx` because the screen was 1,700 lines and these
// eight pieces are the half of it that never changes.
//
// Two rules survive the split unchanged:
//
//   1. **A non-host sees text, not a disabled input.** A disabled stepper
//      invites a press and then says no; text says what the room is set to and
//      leaves it there. That is why `ReadOnlyRow` and `StaticValue` exist at all.
//   2. **The card is the grouping, so nothing inside it draws its own border.**
//      Nesting a bordered, filled row inside a filled card composites two washes
//      and lands outside what the palette was solved for (CLAUDE.md).
// ---------------------------------------------------------------------------

import { useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { Slider } from '../../../components/primitives/Slider.js';
import { Stepper } from '../../../components/primitives/Stepper.js';

/** The bounds the server enforces in `sanitizeSettings`, restated because they
 *  are module-private there. A control must never offer a value it would clamp. */
export const TIMER_MIN_SECONDS = 5;
export const TIMER_MAX_SECONDS = 300;
export const TIMER_STEP_SECONDS = 5;

export const seconds = (value: number) => `${value}s`;
export const spokenSeconds = (value: number) => (value === 1 ? '1 second' : `${value} seconds`);

export function SectionLabel({ children }: { children: ReactNode }): ReactElement {
  return <span className="label-mono">{children}</span>;
}

export function Badge({
  tone,
  children,
}: {
  tone: 'host' | 'muted';
  children: ReactNode;
}): ReactElement {
  return (
    <span
      className={`rounded-[5px] px-2 py-[5px] font-mono text-[10px] font-semibold tracking-[0.08em] uppercase ${
        tone === 'host' ? 'bg-line-strong text-ink-on-fill' : 'bg-raised text-ink-dim'
      }`}
    >
      {children}
    </span>
  );
}

/**
 * Content that grows into place rather than appearing under the cursor.
 *
 * A message that mounts mid-drag moves everything below it by its own height in
 * one frame, which on a phone is the footer sliding out from under the thumb
 * about to press it. The row animates from `0fr` to `1fr` instead, so the shift
 * is 200ms of motion the eye can follow rather than a jump.
 *
 * The children stay mounted and are made `inert` while closed, so nothing inside
 * is tabbable or readable while it is a zero-height box.
 */
export function Reveal({ open, children }: { open: boolean; children: ReactNode }): ReactElement {
  return (
    <div
      // **The open state has to be in the DOM, because the children are there
      // either way.** A closed `Reveal` is a zero-height box around a mounted,
      // inert subtree, so `queryByTestId` finds the thing inside it whether it
      // is on screen or not — and a test asserting "the room is told this"
      // passes against a version that never opens. jsdom computes no layout, so
      // the row size is not readable either.
      data-reveal-open={open || undefined}
      className="grid transition-[grid-template-rows] duration-200 ease-out"
      style={{ gridTemplateRows: open ? '1fr' : '0fr' }}
    >
      <div className="overflow-hidden" inert={!open}>
        {children}
      </div>
    </div>
  );
}

/** One setting, where the row is the whole card. */
export function SettingRow({
  label,
  hint,
  control,
  wide,
}: {
  label: string;
  hint?: string;
  control: ReactNode;
  wide?: boolean;
}): ReactElement {
  return (
    <div
      className={`flex items-center gap-3 rounded-card border border-line bg-surface px-4 py-3.5 ${
        wide ? 'lg:col-span-2' : ''
      }`}
    >
      <div className="flex min-w-0 flex-col gap-1">
        <span className="text-[14px] font-medium text-ink">{label}</span>
        {hint ? <span className="text-[12px] text-ink-muted">{hint}</span> : null}
      </div>
      <div className="ml-auto flex shrink-0 items-center gap-2.5">{control}</div>
    </div>
  );
}

/** A card holding several settings that belong together. */
export function SettingsCard({
  children,
  wide,
}: {
  children: ReactNode;
  wide?: boolean;
}): ReactElement {
  return (
    <div
      className={`flex flex-col gap-2.5 rounded-card border border-line bg-surface px-4 py-3 ${
        wide ? 'lg:col-span-2' : ''
      }`}
    >
      {children}
    </div>
  );
}

/** Holds one setting off the next inside a card. */
export function CardRule(): ReactElement {
  return <div aria-hidden="true" className="h-px bg-line" />;
}

/**
 * A timer setting, as a slider.
 *
 * `readOnly` renders the same geometry with nothing to grab, for the players who
 * are not the host: a control that moves under a finger and then snaps back
 * because the server refused it is worse than one that never moved.
 *
 * **It takes no hint, and the prop was removed rather than left unused.** Every
 * one of the six read as a sentence explaining its own label — "How long
 * everybody has on the nine letters" under a slider called Letters round showing
 * 30s — and six of them stacked was most of why this screen was the wordiest in
 * the game. A label and a value is the whole of what a clock has to say.
 */
export function TimerField({
  label,
  value,
  onChange,
  editable,
  min = TIMER_MIN_SECONDS,
  max = TIMER_MAX_SECONDS,
  format = seconds,
  describeValue = spokenSeconds,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  editable: boolean;
  min?: number;
  max?: number;
  format?: (value: number) => string;
  describeValue?: (value: number) => string;
}): ReactElement {
  return (
    <Slider
      label={label}
      showLabel
      format={format}
      describeValue={describeValue}
      value={value}
      onChange={onChange}
      min={min}
      max={max}
      step={TIMER_STEP_SECONDS}
      readOnly={!editable}
    />
  );
}

/** What everyone who is not the host sees in place of a control. */
export function StaticValue({ children }: { children: ReactNode }): ReactElement {
  return <span className="font-mono text-[15px] font-semibold text-ink tabular">{children}</span>;
}

/** A setting read as text, inside a card that already draws its own border. */
export function ReadOnlyRow({
  label,
  hint,
  value,
}: {
  label: string;
  hint?: string;
  value: string;
}): ReactElement {
  return (
    // Keyed by its own label, because the lobby now has more than one read-only
    // row whose value is the word "on" and a test asking for that text alone
    // matches two elements while proving nothing about which setting it read.
    <div data-readonly-row={label} className="flex min-h-11 items-center gap-3">
      <div className="flex min-w-0 flex-col gap-1">
        <span className="text-[14px] font-medium text-ink">{label}</span>
        {hint ? <span className="text-[12px] text-ink-muted">{hint}</span> : null}
      </div>
      <span className="ml-auto shrink-0">
        <StaticValue>{value}</StaticValue>
      </span>
    </div>
  );
}

/**
 * A number that is allowed to be nothing, as one row rather than two.
 *
 * **Off is `null`, and it is the same control.** The player and audience limits
 * were a switch, a stepper and a paragraph each — six rows for two numbers that
 * are both off by default and that most rooms never touch. Here the row is its
 * own on/off: `Off` is a value the stepper can hold, one step below the minimum,
 * so turning a limit on and setting it are the same gesture instead of two.
 *
 * Switching on lands at `min` rather than at some remembered number, and `min`
 * is the caller's business — the player limit passes the size of the room, so a
 * limit can never arrive smaller than the people already in it.
 */
export function LimitRow({
  label,
  value,
  min,
  max,
  step,
  editable,
  onChange,
}: {
  label: string;
  value: number | null;
  min: number;
  max: number;
  step?: number;
  editable: boolean;
  onChange: (next: number | null) => void;
}): ReactElement {
  // The off position is one WHOLE STEP below the minimum, not one unit below.
  // `Stepper` nudges by `step` and clamps, so with a step of five an off
  // position at `min - 1` would send the first press to `min + 4` — the limit
  // would switch on at a number nobody chose. A step below, the first press
  // lands exactly on `min` and the one after it comes straight back off.
  const stride = step ?? 1;
  const off = min - stride;
  return (
    <div data-limit-row={label} className="flex min-h-11 items-center gap-3">
      <span className="text-[14px] font-medium text-ink">{label}</span>
      <span className="ml-auto shrink-0">
        {editable ? (
          <Stepper
            label={label}
            showLabel={false}
            // The stepper is a number line and this puts "no limit" at the
            // bottom of it, which is where somebody stepping down through
            // smaller and smaller limits expects to arrive — rather than at a
            // switch somewhere else on the card.
            value={value ?? off}
            min={off}
            max={max}
            step={step}
            format={(next) => (next < min ? 'Off' : String(next))}
            onChange={(next) => onChange(next < min ? null : next)}
          />
        ) : (
          <StaticValue>{value === null ? 'Off' : String(value)}</StaticValue>
        )}
      </span>
    </div>
  );
}

/**
 * A named, collapsible band of settings inside Advanced.
 *
 * **Advanced was one flat grid about 1200 pixels tall**, which is the shape
 * Isaac reported as taking up so much space: ten cards with nothing between them
 * saying what any of them is about, so finding one meant reading all of them.
 * Grouping alone would not have fixed the height, and collapsing alone would not
 * have made it findable; this does both, and the closed state is the default
 * because a host who opened Advanced was looking for ONE thing.
 *
 * `Reveal` rather than `<details>`: the children stay mounted either way, which
 * is what lets a test assert on a control inside a closed group — and, more to
 * the point, what stops a group's contents remounting and losing focus every
 * time somebody opens it.
 */
export function SettingsGroup({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): ReactElement {
  const [open, setOpen] = useState(false);
  return (
    <section data-settings-group={title} className="flex flex-col lg:col-span-2">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="flex min-h-11 items-center gap-2 border-b border-line text-left"
      >
        <span className="font-mono text-[11px] font-semibold tracking-[0.08em] text-ink-muted uppercase">
          {title}
        </span>
        <span
          aria-hidden="true"
          className={`ml-auto text-ink-dim transition-transform duration-200 ease-out ${
            open ? 'rotate-90' : ''
          }`}
        >
          ›
        </span>
      </button>
      <Reveal open={open}>
        <div className="grid grid-cols-1 gap-2.5 pt-2.5 lg:grid-cols-2 lg:gap-3">{children}</div>
      </Reveal>
    </section>
  );
}
